/**
 * useContextMenu — the composable half of the right-click menu (DESIGN-SYSTEM.md §5).
 *
 * The presentational {@link ContextMenu} draws the popup/sheet; this hook owns
 * the boilerplate every surface used to hand-roll: open state, pointer coords,
 * phone-sheet detection, native-menu suppression, and empty-menu no-op. A
 * surface just wires `onContextMenu={(e) => menu.open(e, itemsFor(target))}`
 * and renders `{menu.node}`.
 *
 * For a whole element whose menu doesn't depend on sub-targets, prefer the
 * {@link ContextMenuArea} wrapper below.
 */
import {
  useCallback,
  useState,
  type ReactElement,
  type ReactNode,
  createElement,
  type ElementType,
} from "react";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu.js";

/** The subset of a right-click event the hook needs — React or native. */
export interface ContextMenuEvent {
  preventDefault(): void;
  stopPropagation(): void;
  readonly clientX: number;
  readonly clientY: number;
}

type Items = readonly ContextMenuItem[] | (() => readonly ContextMenuItem[]);

function resolveItems(items: Items): readonly ContextMenuItem[] {
  return typeof items === "function" ? items() : items;
}

/** True when the primary pointer is coarse (touch) — render a bottom sheet. */
function coarsePointer(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(pointer: coarse)").matches
    : false;
}

export interface UseContextMenuResult {
  /**
   * Open the menu at the event's pointer. Suppresses the native menu and stops
   * propagation (an outer area shouldn't also fire). Resolves `items` lazily;
   * an empty list opens nothing — the element simply has no actions right now.
   */
  readonly open: (e: ContextMenuEvent, items: Items) => void;
  /**
   * Open at explicit client coords — for imperative callers that already hold a
   * point rather than the event (e.g. board node/edge handlers that thread
   * `{x, y}` up from SpaceRenderer). Native suppression is the originating
   * handler's job; an empty list no-ops just like {@link open}.
   */
  readonly openAt: (x: number, y: number, items: Items) => void;
  readonly close: () => void;
  readonly isOpen: boolean;
  /** The live <ContextMenu>, or null when closed. Render it once per surface. */
  readonly node: ReactElement | null;
}

/**
 * @param sheet force the presentation (`true` sheet / `false` popup). Omit to
 * auto-detect from the pointer at open time.
 */
export function useContextMenu(opts?: { readonly sheet?: boolean }): UseContextMenuResult {
  const forced = opts?.sheet;
  const [state, setState] = useState<{
    x: number;
    y: number;
    sheet: boolean;
    items: readonly ContextMenuItem[];
  } | null>(null);

  const close = useCallback(() => setState(null), []);

  const openAt = useCallback(
    (x: number, y: number, items: Items) => {
      const resolved = resolveItems(items);
      if (resolved.length === 0) return;
      setState({ x, y, sheet: forced ?? coarsePointer(), items: resolved });
    },
    [forced],
  );

  const open = useCallback(
    (e: ContextMenuEvent, items: Items) => {
      e.preventDefault();
      e.stopPropagation();
      openAt(e.clientX, e.clientY, items);
    },
    [openAt],
  );

  // `node` is a fresh element every render (it must reflect the live `state`),
  // so there's nothing to memoize — `open`/`close` are already stable via
  // useCallback and consumers destructure what they need.
  const node = state ? (
    <ContextMenu x={state.x} y={state.y} items={state.items} sheet={state.sheet} onClose={close} />
  ) : null;

  return { open, openAt, close, isOpen: state !== null, node };
}

export interface ContextMenuAreaProps {
  /** The menu for this element; a function is resolved at open time. */
  readonly items: Items;
  readonly children?: ReactNode;
  readonly className?: string;
  /** Wrapper tag — defaults to a `<div>`. */
  readonly as?: ElementType;
  /** Force sheet/popup; omit to auto-detect. */
  readonly sheet?: boolean;
}

/**
 * Wraps an element so a right-click anywhere inside it opens `items`. The
 * simplest way to give a surface a menu — no state, no render plumbing.
 */
export function ContextMenuArea({
  items,
  children,
  className,
  as = "div",
  sheet,
  ...rest
}: ContextMenuAreaProps & Record<string, unknown>): ReactElement {
  const menu = useContextMenu({ sheet });
  return createElement(
    as,
    { className, onContextMenu: (e: ContextMenuEvent) => menu.open(e, items), ...rest },
    children,
    menu.node,
  );
}
